import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import hackingPandaImg from '@/assets/hacking-panda.png';
import { openCommandMenu } from '@/components/command-menu';
import { recordVisit, useVisitHistory, type VisitEntry } from '@/hooks/useVisitHistory';
import { useAuth } from '@/lib/auth-context';
import { pages } from '@/router';

export const meta = {
  label: 'Home',
  order: 0,
};

const PINNED_TOOLS_STORAGE_KEY = 'dev-console:pinned-tools';
const DEFAULT_PINNED_PATHS = ['/jobs', '/job-worker-traces', '/postgres-jobs', '/workflows'];

const TYPE_COLOR: Record<VisitEntry['type'], string> = {
  page: 'bg-neutral-50 text-neutral-700',
  subpage: 'bg-neutral-50 text-neutral-700',
  patient: 'bg-blue-50 text-blue-700',
  provider: 'bg-green-50 text-green-700',
  job: 'bg-yellow-50 text-yellow-700',
  workflow: 'bg-orange-50 text-orange-700',
  queue: 'bg-yellow-50 text-yellow-700',
};

function todayFormatted() {
  return new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
}

function getPageType(path: string): VisitEntry['type'] {
  if (path.startsWith('/jobs') || path === '/job-worker-traces' || path === '/postgres-jobs') return 'job';
  if (path.startsWith('/workflows')) return 'workflow';
  return 'page';
}

function readPinnedPaths(): string[] {
  try {
    const raw = localStorage.getItem(PINNED_TOOLS_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as string[]) : DEFAULT_PINNED_PATHS;
  } catch {
    return DEFAULT_PINNED_PATHS;
  }
}

function writePinnedPaths(paths: string[]) {
  localStorage.setItem(PINNED_TOOLS_STORAGE_KEY, JSON.stringify(paths));
}

function OmniSearchLauncher() {
  return (
    <button
      className='flex w-full items-center justify-between rounded-md border border-neutral-200 bg-white px-3 py-2 text-left text-sm text-neutral-500 shadow-sm hover:border-neutral-300 hover:text-neutral-700 focus:border-neutral-400 focus:outline-none'
      onClick={openCommandMenu}
      type='button'
    >
      <span>Search pages, subpages, queues, patients, providers...</span>
      <kbd className='rounded border border-neutral-200 bg-neutral-50 px-1.5 py-0.5 text-[10px] text-neutral-400'>
        Cmd K
      </kbd>
    </button>
  );
}

function PinnedTools() {
  const { mostVisited } = useVisitHistory();
  const [pinnedPaths, setPinnedPaths] = useState(readPinnedPaths);
  const pinnedPathSet = useMemo(() => new Set(pinnedPaths), [pinnedPaths]);
  const toolPages = useMemo(() => pages.filter((page) => page.path !== '/'), []);
  const pinnedTools = toolPages.filter((page) => pinnedPathSet.has(page.path));
  const availableTools = toolPages.filter((page) => !pinnedPathSet.has(page.path));
  const suggestions = mostVisited(5).filter((entry) => !pinnedPathSet.has(entry.path));

  function updatePinned(nextPaths: string[]) {
    setPinnedPaths(nextPaths);
    writePinnedPaths(nextPaths);
  }

  function pin(path: string) {
    updatePinned([...pinnedPaths, path]);
  }

  function unpin(path: string) {
    updatePinned(pinnedPaths.filter((candidate) => candidate !== path));
  }

  return (
    <div className='space-y-5'>
      <div>
        <h3 className='font-mediumbold text-sm text-neutral-900'>Pinned Tools</h3>
        <p className='mt-1 text-xs text-neutral-500'>Keep your most-used console pages close.</p>
      </div>

      <div className='space-y-1'>
        {pinnedTools.map((tool) => {
          const type = getPageType(tool.path);
          return (
            <div key={tool.path} className='hover:bg-neutral-25 group flex items-center gap-2 rounded-md px-2 py-1.5'>
              <Link
                className='flex min-w-0 flex-1 items-center gap-2 text-sm'
                onClick={() => recordVisit(tool.path, tool.label, type)}
                to={tool.path}
              >
                <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${TYPE_COLOR[type]}`}>
                  {type}
                </span>
                <span className='truncate text-neutral-900'>{tool.label}</span>
              </Link>
              <button
                aria-label={`Unpin ${tool.label}`}
                className='rounded px-1 text-neutral-300 opacity-0 hover:bg-neutral-100 hover:text-neutral-700 group-hover:opacity-100'
                onClick={() => unpin(tool.path)}
                title={`Unpin ${tool.label}`}
                type='button'
              >
                ×
              </button>
            </div>
          );
        })}

        {pinnedTools.length === 0 && <p className='px-2 text-sm text-neutral-400'>No pinned tools yet.</p>}
      </div>

      {availableTools.length > 0 && (
        <label className='block text-xs font-medium uppercase tracking-wide text-neutral-400'>
          Add tool
          <select
            className='mt-2 w-full rounded-md border border-neutral-200 bg-white px-2 py-1.5 text-sm normal-case tracking-normal text-neutral-700 focus:border-neutral-400 focus:outline-none'
            onChange={(event) => {
              if (!event.target.value) return;
              pin(event.target.value);
              event.target.value = '';
            }}
            value=''
          >
            <option value=''>Choose a page...</option>
            {availableTools.map((tool) => (
              <option key={tool.path} value={tool.path}>
                {tool.label}
              </option>
            ))}
          </select>
        </label>
      )}

      {suggestions.length > 0 && (
        <div>
          <p className='mb-2 text-xs font-medium uppercase tracking-wide text-neutral-400'>Suggested from usage</p>
          <div className='space-y-1'>
            {suggestions.map((entry) => (
              <button
                key={entry.path}
                className='hover:bg-neutral-25 flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm'
                onClick={() => pin(entry.path)}
                type='button'
              >
                <span className='truncate text-neutral-700'>{entry.label}</span>
                <span className='text-xs text-neutral-400'>Pin</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const GITHUB_URL = 'https://github.com/long-view-labs/mono-repo/tree/main/dev-console';

function About() {
  return (
    <div className='h-full rounded-lg border border-neutral-200 bg-white p-5 text-sm text-neutral-600'>
      <h3 className='font-mediumbold mb-3 text-neutral-900'>About</h3>
      <p className='mb-2'>
        Internal tool for looking up and debugging patient, provider, and operational data. Connects to the database in{' '}
        <span className='font-medium text-neutral-800'>read-only mode</span>.
      </p>
      <ul className='mb-2 list-inside list-disc space-y-1'>
        <li>Search and view patient and provider details</li>
        <li>Browse job queues, DLQ messages, and the job registry</li>
        <li>Inspect provider calendars, licensure, and credentialing</li>
      </ul>
      <p>
        This is a living tool.{' '}
        <a
          className='font-medium text-blue-600 underline hover:text-blue-800'
          href={GITHUB_URL}
          rel='noopener noreferrer'
          target='_blank'
        >
          View on GitHub
        </a>
      </p>
    </div>
  );
}

export const HomePage = () => {
  const { user } = useAuth();
  const name = user?.name?.split(' ')[0] ?? '';

  return (
    <div>
      <div className='mb-8'>
        <p className='text-lg text-neutral-500'>
          Welcome, {name || 'there'}! &middot; {todayFormatted()}
        </p>
      </div>

      <div className='flex flex-col gap-6 md:flex-row md:gap-10'>
        <div className='min-w-0 flex-1 space-y-6'>
          <OmniSearchLauncher />

          <div className='grid gap-6 2xl:grid-cols-[minmax(320px,420px)_minmax(0,1fr)]'>
            <div className='min-w-0 flex-1'>
              <About />
            </div>
            <div className='min-w-0'>
              <img src={hackingPandaImg} alt='' className='h-auto w-full max-w-[720px] rounded-lg object-contain' />
            </div>
          </div>
        </div>

        <div className='w-full shrink-0 md:w-64'>
          <PinnedTools />
        </div>
      </div>
    </div>
  );
};
